import { endOfDay, format, startOfDay, subDays } from 'date-fns'

export type DateRange = { from: number | null; to: number | null }

/**
 * Which end of a window a reader is after. Looking back, the most recent is what matters; looking
 * forward, the soonest is. Left unset the reader's own sorting stands.
 */
export type DatePresetSort = 'asc' | 'desc'

export type DatePreset = { id: string; labelKey: string; toRange: (now: number) => DateRange; sort?: DatePresetSort }

/** The column filter value for a date column: the picked preset plus the instants it resolved to. */
export type DateFilterValue = DateRange & { presetId: string }

/** Not a window of its own but a pair of dates the reader fills in, so it resolves to nothing until they do. */
export const CUSTOM_DATE_PRESET = 'custom'

export const ANYTIME_PRESET = 'anytime'

const DAY_MS = 86_400_000

export const ANYTIME_RANGE: DateRange = { from: null, to: null }

/** How a date input holds one end of a stated window, and so how the URL writes it down. */
const DATE_INPUT_FORMAT = 'yyyy-MM-dd'

function resolveRelativeRange(ms: number): (now: number) => DateRange {
  return (now) => ({ from: now - ms, to: now })
}

function resolveCalendarDayRange(daysAgo: number): (now: number) => DateRange {
  // date-fns rather than hand-rolled arithmetic: a day is not always 86,400,000 ms, so "today" would
  // over- or under-reach on the two days a year the clocks change.
  return (now) => {
    const day = subDays(now, daysAgo)
    return { from: startOfDay(day).getTime(), to: endOfDay(day).getTime() }
  }
}

export const DATE_PRESETS: DatePreset[] = [
  { id: ANYTIME_PRESET, labelKey: 'TABLE.DATE_ANYTIME', toRange: () => ANYTIME_RANGE },
  { id: 'past', labelKey: 'TABLE.DATE_PAST', toRange: (now) => ({ from: null, to: now }), sort: 'desc' },
  { id: 'upcoming', labelKey: 'TABLE.DATE_UPCOMING', toRange: (now) => ({ from: now, to: null }), sort: 'asc' },
  { id: CUSTOM_DATE_PRESET, labelKey: 'TABLE.DATE_CUSTOM', toRange: () => ANYTIME_RANGE },
  { id: 'today', labelKey: 'TABLE.DATE_TODAY', toRange: resolveCalendarDayRange(0) },
  { id: 'yesterday', labelKey: 'TABLE.DATE_YESTERDAY', toRange: resolveCalendarDayRange(1) },
  { id: '1h', labelKey: 'TABLE.DATE_LAST_1H', toRange: resolveRelativeRange(3_600_000) },
  { id: '3h', labelKey: 'TABLE.DATE_LAST_3H', toRange: resolveRelativeRange(10_800_000) },
  { id: '6h', labelKey: 'TABLE.DATE_LAST_6H', toRange: resolveRelativeRange(21_600_000) },
  { id: '1d', labelKey: 'TABLE.DATE_LAST_1D', toRange: resolveRelativeRange(DAY_MS) },
  { id: '2d', labelKey: 'TABLE.DATE_LAST_2D', toRange: resolveRelativeRange(2 * DAY_MS) },
  { id: '3d', labelKey: 'TABLE.DATE_LAST_3D', toRange: resolveRelativeRange(3 * DAY_MS) },
  { id: '7d', labelKey: 'TABLE.DATE_LAST_7D', toRange: resolveRelativeRange(7 * DAY_MS) },
  { id: '14d', labelKey: 'TABLE.DATE_LAST_14D', toRange: resolveRelativeRange(14 * DAY_MS) },
  { id: '30d', labelKey: 'TABLE.DATE_LAST_30D', toRange: resolveRelativeRange(30 * DAY_MS) },
  { id: '90d', labelKey: 'TABLE.DATE_LAST_90D', toRange: resolveRelativeRange(90 * DAY_MS) },
  { id: '365d', labelKey: 'TABLE.DATE_LAST_365D', toRange: resolveRelativeRange(365 * DAY_MS) },
]

/** Resolved once when the filter is applied, then stored on it, so no two readers can disagree. */
export function resolveDateRange(presetId: string, now: number): DateRange {
  return DATE_PRESETS.find((preset) => preset.id === presetId)?.toRange(now) ?? ANYTIME_RANGE
}

export function isDatePreset(presetId: string): boolean {
  return DATE_PRESETS.some((preset) => preset.id === presetId)
}

/**
 * The whole of the day at each end, so a reader who names one day gets that day. Either end may be
 * left open, which reads as "from then on" or "up to then".
 */
export function resolveCustomRange(from: string, to: string): DateRange {
  const start = from === '' ? null : new Date(`${from}T00:00:00`).getTime()
  const end = to === '' ? null : new Date(`${to}T23:59:59.999`).getTime()
  return { from: Number.isNaN(start ?? 0) ? null : start, to: Number.isNaN(end ?? 0) ? null : end }
}

/** The day an instant falls on where the reader is, which is the day they named it by. */
export function toDateInputValue(time: number): string {
  return format(time, DATE_INPUT_FORMAT)
}
